import { useEffect, useState, useRef, useCallback } from "react";
import { api, mediaUrl } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { UploadCloud, Film, Package, GitMerge, Clock, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-[#141417] border border-white/10 rounded-sm p-6" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-center justify-between mb-4">
      <span className="overline">{label}</span>
      <Icon size={18} className={accent ? "text-[#D4AF37]" : "text-[#8E8E9A]"} />
    </div>
    <div className="font-serif text-4xl font-light">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [looks, setLooks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();

  const refresh = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/looks").then((r) => setLooks(r.data));
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000); // poll for processing updates
    return () => clearInterval(t);
  }, [refresh]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please choose a video file");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/videos/upload?title=${encodeURIComponent(file.name.replace(/\.[^.]+$/, ""))}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Video uploaded — AI extraction started");
      refresh();
      navigate(`/admin/review/${data.look_id}`);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fade-in">
      <p className="overline mb-2">Studio Overview</p>
      <h1 className="font-serif text-4xl font-light mb-10">Dashboard</h1>

      {/* Upload */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          upload(e.dataTransfer.files?.[0]);
        }}
        className="border-2 border-dashed border-white/15 rounded-sm p-12 text-center mb-12 hover:border-[#D4AF37]/50 transition-colors bg-[#141417]/50"
        data-testid="admin-upload-dropzone"
      >
        {uploading ? (
          <Loader2 size={40} className="mx-auto text-[#D4AF37] animate-spin" />
        ) : (
          <UploadCloud size={40} className="mx-auto text-[#8E8E9A]" />
        )}
        <h3 className="font-serif text-2xl font-light mt-5">Upload a fashion film</h3>
        <p className="text-[#8E8E9A] text-sm mt-2 font-light">
          Frames are extracted server-side and analysed by Gemini 3.1 Pro for products & attributes.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="video/*"
          className="hidden"
          data-testid="admin-video-upload-input"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="admin-choose-video-button"
          className="mt-6 bg-[#D4AF37] text-black px-8 py-3 rounded-full text-sm font-medium hover:bg-[#C5A059] transition-colors disabled:opacity-50"
        >
          {uploading ? "Uploading…" : "Choose Video"}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          <StatCard label="Pending Review" value={stats.looks_pending} icon={Clock} accent />
          <StatCard label="Published Looks" value={stats.looks_published} icon={Film} />
          <StatCard label="Total Products" value={stats.products_total} icon={Package} />
          <StatCard label="Duplicate Flags" value={stats.duplicates_open} icon={GitMerge} accent />
        </div>
      )}

      {/* Recent looks */}
      <div className="flex items-center justify-between mb-5 border-b border-white/10 pb-4">
        <h2 className="font-serif text-2xl font-light">Recent Uploads</h2>
      </div>
      <div className="space-y-3" data-testid="admin-recent-looks">
        {looks.slice(0, 8).map((l) => (
          <button
            key={l.id}
            onClick={() => navigate(`/admin/review/${l.id}`)}
            data-testid={`admin-look-row-${l.id}`}
            className="w-full flex items-center gap-4 p-3 rounded-sm border border-white/5 bg-[#141417] hover:border-[#D4AF37]/40 transition-all text-left"
          >
            <div className="w-14 h-14 rounded-sm overflow-hidden bg-[#0B0B0C] shrink-0">
              {l.hero_thumbnail && <img src={mediaUrl(l.hero_thumbnail)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg font-light truncate">{l.title}</p>
              <p className="text-xs text-[#8E8E9A] font-mono truncate">{l.processing_message || l.id}</p>
            </div>
            <StatusBadge status={l.status} />
          </button>
        ))}
        {looks.length === 0 && <p className="text-[#8E8E9A] font-light">No uploads yet.</p>}
      </div>
    </div>
  );
}
